/**
 * The "Saved" moment on a step's save button (BLU-1376, handover 4.6):
 * after a save succeeds the button reads Saved for `SETUP_SAVED_HOLD_MS`
 * (700ms), or not at all under reduced motion, then returns to idle.
 *
 * `phase(saving)` gives the `SavePrimaryButton` phase; `markSaved()` starts
 * the hold. The timer is cleared on unmount.
 */
import { useCallback, useEffect, useState } from 'react';
import { prefersReducedMotion } from '@/lib/scroll-container';
import { SETUP_SAVED_HOLD_MS } from '../lib/step-state';
import type { SavePhase } from '../components/shell/SavePrimaryButton';

export function useSavedHold() {
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!justSaved) return undefined;
    const timer = window.setTimeout(() => setJustSaved(false), prefersReducedMotion() ? 0 : SETUP_SAVED_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [justSaved]);

  const markSaved = useCallback(() => setJustSaved(true), []);
  const phase = useCallback(
    (saving: boolean): SavePhase => (saving ? 'saving' : justSaved ? 'saved' : 'idle'),
    [justSaved],
  );

  return { markSaved, phase };
}
